import { Container } from '@/components/ui/Container'
import { SectionHeader } from '@/components/ui/SectionHeader'
import { t } from '@/utils/i18n'

// Configuration flag to show/hide investors in the rolling display
const SHOW_INVESTORS = false

interface Partner {
  name: string
  logo?: string
  logoBgColor?: string
}

// All partners data
const strategicPartners: Partner[] = [
  { name: 'ZETRON', logo: '/logo/zetrone_logo.avif' },
]

const researchTechPartners: Partner[] = [
  { name: 'KAIST', logo: '/logo/kaist_logo.png' },
  { name: '해움', logo: '/logo/haeum_logo.png' },
  { name: 'PARITY', logo: '/logo/parity_logo.png' },
]

const investors: Partner[] = [
  { name: '신한벤처투자', logo: '/logo/shinhan_venture_logo.svg', logoBgColor: '#0426a0' },
  { name: '블루포인트파트너스', logo: '/logo/bluepoint_logo.svg' },
  { name: '대전창조경제혁신센터', logo: '/logo/logo_daejeon.png', logoBgColor: '#004ea2' },
  { name: '스타벤처스', logo: '/logo/start_ventures.png' },
]

export function PartnersSection(): HTMLElement {
  const section = document.createElement('section')
  section.className = 'section-container bg-white overflow-hidden'

  const container = Container({
    children: [
      createSectionHeader(),
      createRollingPartners(),
    ],
  })

  section.appendChild(container)
  return section
}

function createSectionHeader(): HTMLElement {
  return SectionHeader({
    title: t('partners.sectionTitle'),
    subtitle: t('partners.sectionSubtitle'),
    marginBottom: 'md',
  })
}

function createRollingPartners(): HTMLElement {
  const wrapper = document.createElement('div')
  wrapper.className = 'relative py-8 overflow-hidden'

  // Combine all partners based on config
  const allPartners = [
    ...strategicPartners,
    ...researchTechPartners,
    ...(SHOW_INVESTORS ? investors : []),
  ]

  // Create the rolling track
  const track = document.createElement('div')
  track.className = 'flex animate-scroll'

  // Duplicate partners for seamless loop
  const partnersToRender = [...allPartners, ...allPartners]

  partnersToRender.forEach(partner => {
    const item = createPartnerItem(partner)
    track.appendChild(item)
  })

  // Add CSS animation
  const style = document.createElement('style')
  style.textContent = `
    @keyframes scroll {
      0% {
        transform: translateX(0);
      }
      100% {
        transform: translateX(-50%);
      }
    }
    .animate-scroll {
      animation: scroll 20s linear infinite;
    }
    .animate-scroll:hover {
      animation-play-state: paused;
    }
  `
  wrapper.appendChild(style)

  // Gradient overlays for fade effect
  const gradientLeft = document.createElement('div')
  gradientLeft.className = 'absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none'

  const gradientRight = document.createElement('div')
  gradientRight.className = 'absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none'

  wrapper.appendChild(gradientLeft)
  wrapper.appendChild(track)
  wrapper.appendChild(gradientRight)

  return wrapper
}

function createPartnerItem(partner: Partner): HTMLElement {
  const item = document.createElement('div')
  item.className = 'flex-shrink-0 mx-8 flex items-center justify-center min-w-[160px] h-20'

  if (partner.logo) {
    // Create a wrapper for logo with optional background
    const logoWrapper = document.createElement('div')
    logoWrapper.className = 'flex items-center justify-center rounded-lg overflow-hidden'

    if (partner.logoBgColor) {
      logoWrapper.style.backgroundColor = partner.logoBgColor
      logoWrapper.className += ' px-4 py-2'
    }

    const img = document.createElement('img')
    img.src = partner.logo
    img.alt = partner.name
    img.className = 'max-h-12 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300'
    img.onerror = () => {
      item.innerHTML = ''
      item.appendChild(createPlaceholder(partner.name))
    }
    logoWrapper.appendChild(img)
    item.appendChild(logoWrapper)
  } else {
    item.appendChild(createPlaceholder(partner.name))
  }

  return item
}

function createPlaceholder(name: string): HTMLElement {
  const placeholder = document.createElement('div')
  placeholder.className = 'text-center px-4 py-2 bg-slate-100 rounded-lg'

  const text = document.createElement('span')
  text.textContent = name
  text.className = 'text-base font-semibold text-slate-400 whitespace-nowrap'

  placeholder.appendChild(text)
  return placeholder
}
